import React, { useState } from 'react';
import './App.css';

function App() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [maxUrls, setMaxUrls] = useState(1000);
  const [includeExternal, setIncludeExternal] = useState(true);

  const formatReachableStatus = (status) => `${status} Reachable`;
  const normalizeInputUrl = (rawUrl) => {
    if (!rawUrl || typeof rawUrl !== 'string') return '';
    const trimmed = rawUrl.trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const handleCheckUrls = async () => {
    const normalizedUrl = normalizeInputUrl(url);
    if (!normalizedUrl) {
      setError('Please enter a valid URL');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);
    setUrl(normalizedUrl);

    try {
      const response = await fetch('https://ravikant.snvdev.com/api/check-urls', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: normalizedUrl,
          maxUrls: Number(maxUrls) || 500,
          crawlConcurrency: 4,
          statusConcurrency: 8,
          useSitemap: true,
          includeExternal,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to check URLs');
      }

      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(err.message || 'An error occurred while checking URLs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>URL Checker</h1>
        <p>Find reachable URLs and broken links from a homepage</p>
      </header>

      <main className="App-main">
        <div className="input-section">
          <input
            type="url"
            placeholder="Enter homepage URL (e.g., https://example.com)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="url-input"
          />
          <button
            onClick={handleCheckUrls}
            disabled={loading}
            className="check-button"
          >
            {loading ? 'Checking...' : 'Check URLs'}
          </button>
        </div>
        <div className="options-row" style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
          <label>
            Max URLs:&nbsp;
            <input
              type="number"
              min="10"
              max="5000"
              step="50"
              value={maxUrls}
              onChange={(e) => setMaxUrls(e.target.value)}
              disabled={loading}
              style={{ width: '90px' }}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={includeExternal}
              onChange={(e) => setIncludeExternal(e.target.checked)}
              disabled={loading}
            />
            &nbsp;Include external URLs
          </label>
        </div>
        {error && <div className="error-message">{error}</div>}

        {loading && <div className="loading">Analyzing URLs...</div>}

        {results && (
          <div className="results-section">
            {results.websiteIssue && (
              <div className="website-issue-notice">
                <p>🚨 {results.message}</p>
                {results.suggestions && (
                  <div className="suggestions">
                    <p><strong>Possible causes:</strong></p>
                    <ul>
                      {results.suggestions.map((suggestion, index) => (
                        <li key={index}>{suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {!results.websiteIssue && results.analysisInfo && (
              <div className="analysis-info">
                <p>ℹ️ {results.analysisInfo.message}</p>
              </div>
            )}
            {results.blockedByProtection && (
              <div className="website-issue-notice">
                <p>🚫 {results.message}</p>
              </div>
            )}
            <div className="results-summary">
              <h2>Results Summary</h2>
              <div className="summary-cards">
                <div className="summary-card success">
                  <h3>200 OK</h3>
                  <span className="count">{results.successUrls.length}</span>
                </div>
                <div className="summary-card error">
                  <h3>404 Not Found</h3>
                  <span className="count">{results.errorUrls.length}</span>
                </div>
                <div className="summary-card total">
                  <h3>Other Status</h3>
                  <span className="count">{results.otherUrls?.length || 0}</span>
                </div>
                <div className="summary-card total">
                  <h3>Total URLs</h3>
                  <span className="count">{results.totalUrls}</span>
                </div>
              </div>
            </div>

            <div className="url-lists">
              <div className="url-list">
                <h3>📋 All URLs with Status</h3>
                <div className="url-container">
                  {(results.allUrls || []).map((urlData, index) => (
                    <div key={index} className="url-item">
                      <a href={urlData.url} target="_blank" rel="noopener noreferrer">
                        {urlData.url}
                      </a>
                      <span className="status">
                        {urlData.status === 0 ? `Failed (${urlData.error || 'Unknown error'})` : urlData.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="url-list">
                <h3>✅ Reachable URLs (200 OK)</h3>
                <div className="url-container">
                  {results.successUrls.map((urlData, index) => (
                    <div key={index} className="url-item success">
                      <a href={urlData.url} target="_blank" rel="noopener noreferrer">
                        {urlData.url}
                      </a>
                      <span className="status">{formatReachableStatus(urlData.status)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="url-list">
                <h3>❌ Broken URLs (404)</h3>
                <div className="url-container">
                  {results.errorUrls.map((urlData, index) => (
                    <div key={index} className="url-item error">
                      <a href={urlData.url} target="_blank" rel="noopener noreferrer">
                        {urlData.url}
                      </a>
                      <span className="status">404 Not Found</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="url-list">
                <h3>ℹ️ Other Status URLs</h3>
                <div className="url-container">
                  {(results.otherUrls || []).map((urlData, index) => (
                    <div key={index} className="url-item">
                      <a href={urlData.url} target="_blank" rel="noopener noreferrer">
                        {urlData.url}
                      </a>
                      <span className="status">{urlData.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
